/**
 * 운영콘솔 — 비동기 폼 제출 + Toast 알림.
 * .admin-ops__card-form 을 가로채 fetch + Toast + 카드 인라인 로그로 처리한다.
 * 마이그레이션·롤백·백업 성공 후에는 목록 동기화를 위해 페이지를 새로고침한다.
 */

import { Toast } from "../../utils/Toast";
import { Modal } from "../../utils/Modal";

const FORM_SEL = ".admin-ops__card-form";
const LOG_CLASS = "admin-ops__inline-log";
const RELOAD_KEYS = ["migrate", "rollback", "backup"];
const RELOAD_DELAY_MS = 1400;

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function updateResultBox(label: string, ok: boolean, msg: string, lines: string[]): void {
  let box = document.querySelector<HTMLElement>(".admin-ops__result");
  if (!box) {
    box = document.createElement("div");
    document.querySelector(".admin-ops__tabs")?.after(box);
  }
  box.className = `admin-ops__result ${ok ? "is-ok" : "is-fail"}`;
  box.innerHTML =
    `<h3 class="admin-ops__result-title">` +
    `<i class="fa-solid ${ok ? "fa-circle-check" : "fa-circle-xmark"}" aria-hidden="true"></i> ` +
    `${esc(label)} — ${esc(msg)}</h3>` +
    (lines.length > 0 ? `<pre class="admin-ops__result-log">${esc(lines.join("\n"))}</pre>` : "");
  box.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

function keyFromAction(form: HTMLFormElement): string {
  const m = form.action.match(/\/ops\/([^/]+)\/run/);
  return m?.[1] ?? "";
}

function clearLog(form: HTMLFormElement): void {
  form.parentElement?.querySelector(`.${LOG_CLASS}`)?.remove();
}

function appendLog(form: HTMLFormElement, lines: string[], ok: boolean): void {
  if (lines.length === 0) return;
  const pre = document.createElement("pre");
  pre.className = LOG_CLASS + (ok ? " is-ok" : " is-fail");
  pre.textContent = lines.join("\n");
  form.after(pre);
}

async function runAction(form: HTMLFormElement): Promise<void> {
  const btn = form.querySelector<HTMLButtonElement>("button[type=submit]");
  const origHtml = btn?.innerHTML ?? "";
  const key = keyFromAction(form);

  if (btn) {
    btn.disabled = true;
    btn.innerHTML =
      '<i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> 실행 중...';
  }

  clearLog(form);

  try {
    const resp = await fetch(form.action, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: new FormData(form),
    });

    // 성공 응답은 컨트롤러의 {result,msg,lines}, 에러 응답(미들웨어/예외 — 예: CSRF 419)은
    // 공통 {success,message} 형태라 두 키를 모두 수용한다. 그렇지 않으면 실제 사유가
    // "오류가 발생했습니다" 로 가려진다.
    const data = (await resp.json()) as {
      result?: string;
      success?: boolean;
      msg?: string;
      message?: string;
      lines?: string[];
    };

    const ok = data.result === "success" || data.success === true;
    const msg =
      data.msg ?? data.message ?? (ok ? "완료되었습니다." : "오류가 발생했습니다.");
    const lines = Array.isArray(data.lines) ? data.lines : [];
    const label =
      form.closest(".admin-ops__card")?.querySelector(".admin-ops__card-title")?.textContent?.trim() ?? key;

    if (ok) {
      Toast.success(msg);
    } else {
      Toast.error(msg);
    }

    updateResultBox(label, ok, msg, lines);
    appendLog(form, lines, ok);

    if (ok && RELOAD_KEYS.some((k) => key.includes(k))) {
      setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
    } else if (!ok && resp.status === 419) {
      // CSRF 토큰 만료(세션 교체 등) — 새 토큰을 받기 위해 새로고침
      setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
    }
  } catch {
    Toast.error("요청에 실패했습니다. 네트워크 상태를 확인하세요.");
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = origHtml;
    }
  }
}

export function initOpsConsole(): void {
  const forms = document.querySelectorAll<HTMLFormElement>(FORM_SEL);
  if (forms.length === 0) return;

  forms.forEach((form) => {
    const confirmMsg = form.dataset.confirm;
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      if (confirmMsg) {
        void Modal.confirm(confirmMsg).then((ok) => {
          if (ok) void runAction(form);
        });
      } else {
        void runAction(form);
      }
    });
  });
}

export function initOpsTabs(): void {
  const tabs = document.querySelectorAll<HTMLButtonElement>(
    ".admin-ops__tab[data-tab]"
  );
  const panels = document.querySelectorAll<HTMLElement>(
    ".admin-ops__panel[data-panel]"
  );
  if (tabs.length === 0 || panels.length === 0) return;

  const validIds = Array.from(tabs).map((t) => t.dataset.tab ?? "");

  function activate(id: string): void {
    tabs.forEach((t) => {
      const active = t.dataset.tab === id;
      t.setAttribute("aria-selected", String(active));
      t.classList.toggle("is-active", active);
    });
    panels.forEach((p) => {
      p.hidden = p.dataset.panel !== id;
    });
  }

  const hash = location.hash.slice(1);
  activate(validIds.includes(hash) ? hash : validIds[0] ?? "");

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const id = tab.dataset.tab ?? "";
      history.replaceState(null, "", "#" + id);
      activate(id);
    });
  });
}
